import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import DataLayer from "../utils/dataLayer";

interface SearchArgs {
  term: string;
  limit: number;
  page?: number;
  searchVariant?: string;
}

export interface SearchHit {
  id: string;
  url: string;
  title?: string;
  meta_description: string;
  meta_categories: string[];
  custom_fields: Record<string, string>;
  highlight: string;
  categories: string[];
  document_type: string;
  images: {
    main: string;
    main_b64: string;
    capture: string;
  };
  score: number;
}

interface SearchResponse {
  page: number;
  total_hits: number;
  hits: SearchHit[];
}

interface Suggestion {
  value: string;
}

interface SuggestionArgs {
  term: string;
  size?: number;
  language?: string;
}

interface SuggestionResponse {
  suggestions: Suggestion[];
}

export const AddSearchApiSlice = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: "https://api.addsearch.com/v1/",
  }),
  endpoints: (builder) => ({
    reportClick: builder.mutation<void, { keyword: string; id: string; position: number }>({
      query: ({ keyword, id, position }) => ({
        data: {
          action: "click",
          docid: id,
          keyword,
          position,
        },
        method: "POST",
        url: `stats/${process.env.GATSBY_ADDSEARCH_SITE_KEY}`,
      }),
    }),
    search: builder.query<SearchResponse, SearchArgs>({
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 1) {
          currentCache.hits.push(...newItems.hits);

          return currentCache;
        }

        return newItems;
      },
      onQueryStarted(args) {
        if (args.page && args.page === 1) {
          DataLayer.pushEvent("pp_search_request", {
            dimension_search_variant: args.searchVariant,
            event_value: args.term,
          });
        }
      },
      query: ({ term, limit, page = 1 }) => ({
        params: {
          fuzzy: "auto",
          limit,
          page,
          term,
          ...typeof process.env.GATSBY_ADDSEARCH_SEARCH_CATEGORY !== "undefined"
            ? { categories: process.env.GATSBY_ADDSEARCH_SEARCH_CATEGORY } : {},
        },
        url: `search/${process.env.GATSBY_ADDSEARCH_SITE_KEY}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => endpointName + queryArgs.term,
    }),
    suggestions: builder.query<SuggestionResponse, SuggestionArgs>({
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      query: ({ term }) => ({
        params: {
          size: 3,
          term,
        },
        url: `suggest/${process.env.GATSBY_ADDSEARCH_SITE_KEY}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => (
        endpointName + queryArgs.term
      ),
    }),
  }),
  reducerPath: "addSearchApi",
});

export const {
  useSearchQuery,
  useReportClickMutation,
  useSuggestionsQuery,
} = AddSearchApiSlice;

export default AddSearchApiSlice;
